import React, { useState, useEffect } from 'react';
import { formatDistanceToNow } from 'date-fns';
import trLocale from 'date-fns/locale/tr';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import { useWebSocket } from '../utils/WebSocketContext';
import '../styles/Notifications.css';

const Notifications = () => {
  const [notifications, setNotifications] = useState([]);
  const ws = useWebSocket();
  const navigate = useNavigate();

  const fetchNotifications = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        console.error('Token mevcut değil');
        return;
      }

      const response = await api.get('/notifications', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      const sortedNotifications = response.data.sort(
        (a, b) => new Date(b.timestamp) - new Date(a.timestamp)
      );
      setNotifications(sortedNotifications);
    } catch (error) {
      console.error('Error fetching notifications:', error);
    }
  };

  useEffect(() => {
    fetchNotifications();
  }, []);

  useEffect(() => {
    if (ws) {
      ws.onmessage = (event) => {
        const newNotification = JSON.parse(event.data);
        setNotifications(prev => [newNotification, ...prev]);
      };
    }
  }, [ws]);

  const getIcon = (type) => {
    const icons = {
      like: <span className="notification-icon like">❤️</span>,
      comment: <span className="notification-icon comment">💬</span>,
      follow: <span className="notification-icon follow">✅</span>,
      read: <span className="notification-icon star">⭐</span>,
      'new-chapter': <span className="notification-icon new-chapter">✍️</span>,
      default: <span className="notification-icon">🔔</span>,
    };
    return icons[type] || icons.default;
  };

  const handleNotificationClick = async (notification) => {
    try {
      const token = localStorage.getItem('token');
      
      // Bildirim okunmamışsa okundu olarak işaretle
      if (token && !notification.is_read) {
        // Önce UI'da güncelle
        setNotifications(prev =>
          prev.map(n =>
            n.id === notification.id
              ? { ...n, is_read: true }
              : n
          )
        );

        // Sonra API'ye gönder (asenkron olarak)
        api.put(`/notifications/${notification.id}/mark-read`, {}, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }).catch(error => {
          console.error('Error marking notification as read:', error);
          // Hata durumunda UI'da geri al
          setNotifications(prev =>
            prev.map(n =>
              n.id === notification.id
                ? { ...n, is_read: false }
                : n
            )
          );
        });
      }

      // Yönlendirme işlemi
      switch (notification.type) {
        case 'follow':
          if (notification.sender_id) {
            navigate(`/profile/${notification.sender_id}`);
          }
          break;

        case 'like':
        case 'new-chapter':
        case 'comment':
          if (notification.book_id && notification.chapter_id) {
            navigate(`/read/${notification.book_id}/${notification.chapter_id}`);
          } else if (notification.book_id) {
            navigate(`/book/${notification.book_id}`);
          }
          break;

        case 'read':
          if (notification.book_id) {
            navigate(`/book/${notification.book_id}`);
          }
          break;

        default:
          break;
      }
    } catch (error) {
      console.error('Error handling notification click:', error);
    }
  };

  const markAllAsRead = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        console.error('Token mevcut değil');
        return;
      }

      await api.put('/notifications/mark-all-read', {}, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      setNotifications(notifications.map(notification => ({
        ...notification,
        is_read: true,
      })));
    } catch (error) {
      console.error('Error marking all notifications as read:', error);
    }
  };

  return (
    <div className="notifications-container">
      <div className="notifications-header">
        <h1>Bildirimler</h1>
        <button onClick={markAllAsRead} className="mark-read-button">
          Tümünü Okundu İşaretle
        </button>
      </div>

      <div className="notifications-list">
        {notifications.map((notification) => (
          <div
            key={notification.id}
            className={`notification-item ${notification.is_read ? 'read' : 'unread'}`}
            onClick={() => handleNotificationClick(notification)}
            style={{ cursor: 'pointer' }}
          >
            {getIcon(notification.type)}
            <div className="notification-content">
              <p>{notification.content}</p>
              <span className="notification-timestamp">
                {notification.timestamp ? (
                  formatDistanceToNow(new Date(notification.timestamp), {
                    addSuffix: true,
                    locale: trLocale
                  })
                ) : (
                  'az önce'
                )}
              </span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default Notifications;